/**
 * useTodoHandlers — UI event handlers for todo item interactions.
 */
import { GoalOperations } from "../goal-state/useGoalEventHandlers";
import { useToast } from "@/hooks/use-toast";
import { usePoints } from "@/hooks/usePoints";
import { Goal } from "@/types/todo";
import { logger } from "@/utils/logger";

export const useTodoHandlers = (operations: GoalOperations) => {
  const { toast } = useToast();
  const { addTaskPoints } = usePoints();
  
  const handleAddTodo = async (goalTitle: string, todoText: string, todoDueDate: string) => {
    logger.debug(`[useTodoHandlers.handleAddTodo] START - Text: "${todoText}" | Goal: "${goalTitle}" | Date: "${todoDueDate}"`);
    
    try {
      if (!todoText) {
        logger.error(`[useTodoHandlers.handleAddTodo] Empty todo text`);
        return Promise.reject(new Error("Task text cannot be empty"));
      }
      
      if (todoText === goalTitle) {
        logger.warn(`[useTodoHandlers.handleAddTodo] Todo text equals goal title`);
      }
      
      logger.debug(`[useTodoHandlers.handleAddTodo] Calling operations.addTodo - Text: "${todoText}" | Goal: "${goalTitle}" | Date: "${todoDueDate}"`);
      return operations.addTodo({ 
        goalTitle, 
        todoText, 
        todoDueDate 
      });
    } catch (error) {
      logger.error(`[useTodoHandlers.handleAddTodo] ERROR:`, error);
      return Promise.reject(error);
    }
  };

  const handleDeleteTodo = async (goalTitle: string, todoId: string) => {
    logger.debug(`[useTodoHandlers.handleDeleteTodo] Deleting todo from goal "${goalTitle}" with ID: "${todoId}"`);
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(todoId)) {
      logger.error(`[useTodoHandlers.handleDeleteTodo] Invalid todoId format: "${todoId}"`);
      toast({
        title: "Error deleting task",
        description: "Invalid task identifier format",
        variant: "destructive",
      });
      return Promise.reject(new Error("Invalid task identifier"));
    }
    
    if (!todoId) {
      logger.error(`[useTodoHandlers.handleDeleteTodo] Empty todoId`);
      toast({
        title: "Error deleting task",
        description: "Missing task identifier",
        variant: "destructive",
      });
      return Promise.reject(new Error("Missing task identifier"));
    }
    
    return operations.deleteTodo({ goalTitle, todoId });
  };

  const handleToggleTodo = async (goalTitle: string, todoId: string, currentCompleted?: boolean) => {
    if (currentCompleted === false) {
      await addTaskPoints(todoId);
    }
    return operations.toggleTodo({ goalTitle, todoId });
  };

  const handleUpdateTodo = async (goalTitle: string, todoId: string, newText: string) => {
    logger.debug(`[useTodoHandlers.handleUpdateTodo] Updating todo "${todoId}" in goal "${goalTitle}"`);
    return operations.updateTodo({ goalTitle, todoId, newText });
  };

  return {
    handleAddTodo,
    handleDeleteTodo,
    handleToggleTodo,
    handleUpdateTodo
  };
};
